import { type FormEvent, useEffect, useState } from 'react'
import {
  Check,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  Mail,
  RefreshCw,
  Search,
  Shield,
  ShieldAlert,
  ShieldCheck,
  User,
  UserCheck,
  UserPlus,
  Users,
  X,
} from 'lucide-react'
import { usersApi } from '@/api/endpoints'
import { useAuth } from '@/features/auth/AuthContext'
import type { CreateUserPayload, UserItem } from '@/types'

interface UserManagementModalProps {
  open: boolean
  onClose: () => void
}

const AVAILABLE_ROLES = [
  {
    code: 'ADMIN',
    name: 'Administrator',
    description: 'Full system management: users, assets, locations & exports.',
    icon: ShieldCheck,
    badgeBg: 'bg-[#1e40af]/15 text-[#1e40af] border-[#1e40af]/30',
  },
  {
    code: 'ASSET_MANAGER',
    name: 'Asset Manager',
    description: 'Can register, update, physically verify, and export assets.',
    icon: Shield,
    badgeBg: 'bg-[#065f46]/15 text-[#065f46] border-[#065f46]/30',
  },
  {
    code: 'AUDITOR',
    name: 'Auditor',
    description: 'Can inspect assets, review history, audit logs, and export.',
    icon: UserCheck,
    badgeBg: 'bg-[#92400e]/15 text-[#92400e] border-[#92400e]/30',
  },
  {
    code: 'USER',
    name: 'Standard User',
    description: 'Can register and manage their own assets & perform scans.',
    icon: User,
    badgeBg: 'bg-[#053736]/15 text-[#053736] border-[#053736]/30',
  },
  {
    code: 'VIEWER',
    name: 'Viewer',
    description: 'Read-only access to view asset registry and specifications.',
    icon: Eye,
    badgeBg: 'bg-[#475569]/15 text-[#475569] border-[#475569]/30',
  },
  {
    code: 'SUPER_ADMIN',
    name: 'Super Administrator',
    description: 'Master unrestricted control over all enterprise systems.',
    icon: ShieldAlert,
    badgeBg: 'bg-[#5b21b6]/15 text-[#5b21b6] border-[#5b21b6]/30',
    superAdminOnly: true,
  },
]

export function UserManagementModal({ open, onClose }: UserManagementModalProps) {
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState<'create' | 'directory'>('create')

  // Form State
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [roleCode, setRoleCode] = useState('USER')
  const [employeeCode, setEmployeeCode] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  // Status State
  const [submitting, setSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // Directory State
  const [usersList, setUsersList] = useState<UserItem[]>([])
  const [loadingList, setLoadingList] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const isSuperAdmin = user?.roles?.includes('SUPER_ADMIN') ?? false

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (open) {
      document.addEventListener('keydown', handleKeyDown)
    }
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose])

  // Fetch users directory when opening or switching to directory tab
  const fetchUsers = async () => {
    try {
      setLoadingList(true)
      const res = await usersApi.list()
      setUsersList(res.items || [])
    } catch {
      // ignore
    } finally {
      setLoadingList(false)
    }
  }

  useEffect(() => {
    if (open) {
      void fetchUsers()
    }
  }, [open])

  if (!open) return null

  const handleGeneratePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%&*'
    let pwd = ''
    for (let i = 0; i < 12; i++) {
      pwd += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    setPassword(pwd)
    setShowPassword(true)
  }

  const handleCreateUser = async (e: FormEvent) => {
    e.preventDefault()
    setErrorMessage(null)
    setSuccessMessage(null)

    if (!fullName.trim() || !email.trim() || !password) {
      setErrorMessage('Please provide Full Name, Email address, and Password.')
      return
    }

    if (password.length < 8) {
      setErrorMessage('Password must be at least 8 characters long.')
      return
    }

    try {
      setSubmitting(true)
      const payload: CreateUserPayload = {
        full_name: fullName.trim(),
        email: email.trim().toLowerCase(),
        password,
        role_code: roleCode,
        employee_code: employeeCode.trim() || undefined,
      }

      const created = await usersApi.create(payload)
      setSuccessMessage(`User "${created.full_name}" registered successfully with role ${roleCode}!`)
      
      // Reset form
      setFullName('')
      setEmail('')
      setPassword('')
      setEmployeeCode('')
      setRoleCode('USER')

      // Refresh list
      void fetchUsers()
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to create user. Please check your inputs.'
      setErrorMessage(msg)
    } finally {
      setSubmitting(false)
    }
  }

  const filteredUsers = usersList.filter((u) => {
    if (!searchQuery.trim()) return true
    const q = searchQuery.toLowerCase()
    return (
      u.full_name?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.roles?.some((r) => r.toLowerCase().includes(q))
    )
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm animate-fade-in transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal Card */}
      <div
        style={{
          background: 'linear-gradient(145deg, #ffffff 0%, #f7fdf4 100%)',
          border: '1.5px solid rgba(194, 232, 176, 0.9)',
          boxShadow: '0 20px 45px -8px rgba(5, 55, 54, 0.3), 0 0 0 1px rgba(110, 190, 72, 0.2)',
        }}
        className="relative w-full max-w-2xl max-h-[90dvh] flex flex-col overflow-hidden rounded-2xl shadow-2xl animate-scale-in z-10"
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between border-b border-slate-200/70 px-6 py-4 bg-white/80 backdrop-blur-md shrink-0">
          <div className="flex items-center gap-2.5">
            <div
              style={{ background: 'linear-gradient(135deg, #10b981 0%, #053736 100%)' }}
              className="flex h-9 w-9 items-center justify-center rounded-xl text-white shadow-sm"
            >
              <Users className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-black text-slate-900 leading-tight">Team & User Management</h2>
              <p className="text-xs font-semibold text-slate-500">Add team members and assign system roles</p>
            </div>
          </div>

          <button
            onClick={onClose}
            aria-label="Close modal"
            className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-900/70 hover:bg-trc-border hover:text-slate-900 transition active:scale-95 cursor-pointer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-slate-200/60 bg-[#f8fafc]/40 px-6 py-2.5 gap-2 shrink-0">
          <button
            onClick={() => {
              setActiveTab('create')
              setErrorMessage(null)
              setSuccessMessage(null)
            }}
            className={`flex items-center gap-2 rounded-lg px-3.5 py-1.5 text-xs font-bold transition cursor-pointer ${
              activeTab === 'create'
                ? 'bg-[#053736] text-[#ffffff] shadow-sm'
                : 'text-slate-500 hover:bg-white/60 hover:text-slate-900'
            }`}
          >
            <UserPlus className="h-3.5 w-3.5" />
            <span>Add New User</span>
          </button>

          <button
            onClick={() => {
              setActiveTab('directory')
              setErrorMessage(null)
              setSuccessMessage(null)
            }}
            className={`flex items-center gap-2 rounded-lg px-3.5 py-1.5 text-xs font-bold transition cursor-pointer ${
              activeTab === 'directory'
                ? 'bg-[#053736] text-[#ffffff] shadow-sm'
                : 'text-slate-500 hover:bg-white/60 hover:text-slate-900'
            }`}
          >
            <Users className="h-3.5 w-3.5" />
            <span>Directory ({usersList.length})</span>
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {activeTab === 'create' ? (
            <form onSubmit={handleCreateUser} className="space-y-4">
              {errorMessage && (
                <div className="rounded-xl border border-red-300 bg-red-50 p-3.5 text-xs font-bold text-red-800 animate-shake">
                  {errorMessage}
                </div>
              )}

              {successMessage && (
                <div className="rounded-xl border border-green-300 bg-green-50 p-3.5 text-xs font-bold text-green-900 flex items-center justify-between animate-fade-in">
                  <span>{successMessage}</span>
                  <button
                    type="button"
                    onClick={() => setActiveTab('directory')}
                    className="underline text-green-800 hover:text-green-950 font-extrabold ml-2"
                  >
                    View in directory →
                  </button>
                </div>
              )}

              {/* Basic Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <div>
                  <label className="block text-xs font-bold text-slate-900 mb-1.5">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
                    <input
                      type="text"
                      required
                      placeholder="e.g. Ramesh Chandra"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 py-2 text-xs font-semibold text-slate-900 placeholder:text-slate-500/70 focus:border-trc-green focus:ring-1 focus:ring-trc-green outline-none transition"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-900 mb-1.5">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
                    <input
                      type="email"
                      required
                      placeholder="user@trcconsulting.org"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 py-2 text-xs font-semibold text-slate-900 placeholder:text-slate-500/70 focus:border-trc-green focus:ring-1 focus:ring-trc-green outline-none transition"
                    />
                  </div>
                </div>
              </div>

              {/* Password & Employee Code */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-xs font-bold text-slate-900">
                      Temporary Password <span className="text-red-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={handleGeneratePassword}
                      className="text-[10.5px] font-bold text-trc-green hover:underline flex items-center gap-1 cursor-pointer"
                    >
                      <KeyRound className="h-3 w-3" />
                      <span>Generate</span>
                    </button>
                  </div>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      required
                      placeholder="At least 8 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-white pl-3 pr-9 py-2 text-xs font-semibold text-slate-900 placeholder:text-slate-500/70 focus:border-trc-green focus:ring-1 focus:ring-trc-green outline-none transition"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2.5 top-2.5 text-slate-500 hover:text-slate-900 transition"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-900 mb-1.5">
                    Employee Code <span className="text-slate-500 font-normal">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. TRC-EMP-1042"
                    value={employeeCode}
                    onChange={(e) => setEmployeeCode(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-900 placeholder:text-slate-500/70 focus:border-trc-green focus:ring-1 focus:ring-trc-green outline-none transition"
                  />
                </div>
              </div>

              {/* Role Selection */}
              <div>
                <label className="block text-xs font-bold text-slate-900 mb-2">
                  Assign User Role <span className="text-red-500">*</span>
                </label>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {AVAILABLE_ROLES.filter((r) => !r.superAdminOnly || isSuperAdmin).map((role) => {
                    const isSelected = roleCode === role.code
                    const IconComp = role.icon
                    return (
                      <div
                        key={role.code}
                        onClick={() => setRoleCode(role.code)}
                        className={`p-3 rounded-xl border transition-all cursor-pointer flex items-start gap-2.5 ${
                          isSelected
                            ? 'border-trc-green bg-[#f8fafc] shadow-sm ring-1 ring-trc-green'
                            : 'border-slate-200 bg-white hover:bg-white/80 hover:border-trc-green/50'
                        }`}
                      >
                        <div
                          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${
                            isSelected
                              ? 'bg-trc-green text-white'
                              : 'bg-slate-100 text-slate-900/70'
                          }`}
                        >
                          <IconComp className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-black text-slate-900">{role.name}</p>
                            {isSelected && <Check className="h-3.5 w-3.5 text-trc-green font-bold" />}
                          </div>
                          <p className="text-[10.5px] font-medium text-slate-500 mt-0.5 leading-snug">
                            {role.description}
                          </p>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Form Action */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full flex items-center justify-center gap-2 rounded-xl py-3 text-xs font-black uppercase tracking-wider text-white transition-all shadow-md cursor-pointer disabled:opacity-50"
                  style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #053736 100%)',
                    boxShadow: '0 4px 14px rgba(5, 55, 54, 0.25)',
                  }}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Registering User...</span>
                    </>
                  ) : (
                    <>
                      <UserPlus className="h-4 w-4" />
                      <span>Create & Assign Role</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          ) : (
            /* Directory View */
            <div className="space-y-3.5">
              {/* Search & Refresh */}
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-2.5 h-3.5 w-3.5 text-slate-500" />
                  <input
                    type="text"
                    placeholder="Search by name, email, or role..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 bg-white pl-8 pr-3 py-2 text-xs font-semibold text-slate-900 placeholder:text-slate-500/70 focus:border-trc-green outline-none"
                  />
                </div>
                <button
                  onClick={fetchUsers}
                  disabled={loadingList}
                  title="Refresh List"
                  className="flex h-8 w-8 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-900 hover:bg-slate-100 transition cursor-pointer"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${loadingList ? 'animate-spin' : ''}`} />
                </button>
              </div>

              {/* Users List */}
              {loadingList && usersList.length === 0 ? (
                <div className="py-12 flex flex-col items-center justify-center text-slate-500 text-xs">
                  <Loader2 className="h-5 w-5 animate-spin mb-2 text-trc-green" />
                  <span>Loading team directory...</span>
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="py-12 text-center text-xs font-bold text-slate-500 bg-white/50 rounded-xl border border-slate-200/60">
                  No team members match your search criteria.
                </div>
              ) : (
                <div className="divide-y divide-trc-border/50 rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
                  {filteredUsers.map((u) => {
                    const primaryRole = u.roles?.[0] || 'USER'
                    const roleConfig =
                      AVAILABLE_ROLES.find((r) => r.code === primaryRole) || AVAILABLE_ROLES[3]
                    const initials = u.full_name
                      .split(' ')
                      .map((n) => n[0])
                      .slice(0, 2)
                      .join('')
                      .toUpperCase() || 'U'

                    return (
                      <div
                        key={u.id}
                        className="p-3 flex items-center justify-between gap-3 hover:bg-slate-100/40 transition"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div
                            style={{ background: 'linear-gradient(135deg, #10b981 0%, #053736 100%)' }}
                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white text-[11px] font-black"
                          >
                            {initials}
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-bold text-slate-900 truncate">{u.full_name}</p>
                            <p className="text-[11px] text-slate-500 truncate">{u.email}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          {u.employee_code && (
                            <span className="hidden sm:inline-block text-[10.5px] font-medium text-slate-500 bg-gray-100 px-2 py-0.5 rounded">
                              {u.employee_code}
                            </span>
                          )}
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-black border ${roleConfig.badgeBg}`}
                          >
                            {roleConfig.name}
                          </span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
